import create, { UseStore } from 'zustand'
import { FormApi, formApi, log } from '..'
import { auth } from '../auth'

import { fetchRequest } from '../'
import { Visit, WorksheetSummary } from '../domain'
import { TreeProps } from '../layout'
import { getCurrentVisitSession, setCurrentVisitSession } from '../visit-context'
import {
  AnnotationApi,
  DocumentFeedback,
  annotationApi,
  getSectionsListRequest,
  getSuperchartFeedbacksRequest,
} from './annotation-api'

import {
  AutoCode,
  BundleVersion,
  DemographicsDocument,
  DocumentBundle,
  DocumentRef,
  KnownDocumentFormats,
} from './document-types'
import { isAutoCode, isDemographics, isSuperChart } from './document-utils'

const service = 'document-content'

type WorksheetResult<T = any> = {
  service?: string
  action?: string
  workflow?: string
  success?: boolean
  result?: T
  errors?: string[]
  alerts?: any[]
}

// TODO: Will we be use post or get ?
function worksheetRequest<T, U>(action: string) {
  return fetchRequest<T, WorksheetResult<U>>({
    method: 'post',
    microservice: true,
    body: (data: T) => ({ service, action, data }),
  })
}

type BundleArgs = {
  worksheetId?: string
  accountNumber?: string
  bundleKey?: string
  principalId?: string
}
export const getDocumenBundelRequest = worksheetRequest<BundleArgs, any>('getdocumentbundle')
export const getCodingProfileRequest = worksheetRequest('getcodingprofile')

type DocumentUrlArgs = {
  filekey: string
  bucket: string
  bundleMasterId?: string
  markAsRead?: boolean
  principalId?: string
}

export const getDocumentUrlRequest = fetchRequest<DocumentUrlArgs, WorksheetResult<{ url: string }>>({
  method: 'post',
  microservice: true,
  body: (data: DocumentUrlArgs) => ({ service: 'document-content', action: 'getdocumenturl', data }),
})

export const getDocumentContentRequest = fetchRequest<DocumentUrlArgs, WorksheetResult<DocumentUrlArgs>>({
  method: 'post',
  microservice: true,
  body: (data: DocumentUrlArgs) => ({
    service: 'document-content',
    action: 'getcontent',
    data: {
      ...{
        principalId: data.principalId || auth?.signedIn?.principalId || auth?.signedIn?.userId,
      },
      ...data,
    },
  }),
})

export const getDocumenBundelRequest2 = fetchRequest<BundleArgs, WorksheetResult<DocumentBundle>>({
  method: 'post',
  microservice: true,
  body: (data: BundleArgs) => ({
    service: 'document-content',
    action: 'getdocumentbundle',
    data: {
      ...{
        principalId: data.principalId || auth?.signedIn?.principalId || auth?.signedIn?.userId,
      },
      ...data,
    },
  }),
})

interface Section {
  [key: string]: string
}
export interface SuperChart {
  headers: string[]
  sections: Section
}
type DocumentData = {
  visit: Visit
  bundles: DocumentBundle[]
  treeData: TreeProps
  physicianTreeData: TreeProps[]
  bundle: DocumentBundle
  bundleKey: string| null |undefined
  content: string | null |undefined
  superChart: SuperChart  | null |undefined
  html: string | null |undefined
  demographics: DemographicsDocument
  pdf: any
  autoCode: AutoCode
  ref: DocumentRef
  error: string
  selectedIds: string[]
  sections: string[]
  feedback: DocumentFeedback[]
  summary: WorksheetSummary
}

export type DocumentApi = {
  data: UseStore<DocumentData>
  bundleVersionForm: FormApi
  openWindow(visit?: Visit): void
  closeWindow(): void
  closeRawDocuments(): void
  getBundles(visit: Visit): void
  getBundleVersion(a: BundleVersion): void
  getContent(ref: DocumentRef): void
  getCodingProfile(): void
  openInTab(ref: DocumentRef): void
  setSummary(a: WorksheetSummary): void
  getFeedback(bundleKey: string): DocumentFeedback[]
  getAnnotationApi(section: string, text: string): AnnotationApi
  setWorkflow(w: string): void
  updateFeedback(bundleKey: string): Promise<DocumentFeedback[] | undefined>
  validateAccount(a: DocumentBundle, visit: Visit): void
}

export const feedbackEnabled = true

export const clearDocuments = create<{ show: boolean }>(() => ({ show: true }))

function createDocumentApi(): DocumentApi {
  const data = create<DocumentData>(() => ({
    visit: null,
    bundles: null,
    treeData: null,
    physicianTreeData: [],
    bundle: null,
    selectedIds: [],
    content: '',
    superChart: null,
    sections: null,
    feedback: null,
    demographics: null,
    html: null,
    pdf: null,
    autoCode: null,
    ref: null,
    error: null,
    bundleKey: null,
    summary: null,
  }))

  let currentWindow: Window

  let workflow = 'ED_Coding_USACS'
  function setWorkflow(w: string) {
    workflow = w || workflow
  }

  function openWindow(v: Visit) {
    if (v) {
      v.workflow = v.workflow || workflow
      setCurrentVisitSession(v, false)
      data.setState({ visit: v })

      if (!currentWindow || currentWindow.closed) {
        currentWindow = window.open('/documents', '_blank')
      }
      if (currentWindow) {
        currentWindow.onbeforeunload = (_) => {
          currentWindow = null
          closeChannel.postMessage('post')
          closeRawDocuments()
        }
        currentWindow.name = v.visitId
        currentWindow.postMessage({ visit_id: v.visitId })
      }
      window.postMessage({ visit_id: v.visitId })
    }
    closeRawDocuments()
  }

  function closeWindow() {
    if (currentWindow) {
      currentWindow.close()
    }
    closeRawDocuments()
  }

  function validateAccount(current: DocumentBundle, visit: Visit) {
    if (current && visit.worksheetId) {
      if (current.worksheetId !== visit.worksheetId) {
        console.info('account bundle mismatch', current, visit)
        getBundles(visit)
        return
      }
    }
    const p = window?.parent
    if (p) {
      const { worksheetId } = current
      accountValidation.postMessage({ event: 'validateAccount', account: visit.accountNumber, worksheetId })
    }
  }

  async function request<T = any>(a: () => Promise<any>, b: (a: T) => void, meta?: object) {
    data.setState({ error: null })

    function onError(e: any) {
      const error = Array.isArray(e) ? e.join('') : (e || 'Unknown Error').toString()
      data.setState({ error })
      log.error({ error, meta })
      alert(
        'There was an error requesting documents. Please refresh the page to ensure you are referencing the correct account documentation.',
      )
    }
    try {
      const response: WorksheetResult = await a()
      if (response.errors) {
        onError(response.errors)
      } else {
        b(response.result)
      }
    } catch (e) {
      onError(e)
    }
  }

  async function getBundles(a: Visit) {
    if (!a.wsSummaries?.length) {
      return
    }

    closeRawDocuments()
    data.setState({ bundle: null })

    feedbackEnabled &&
      getSectionsListRequest().then((a) => {
        if (a?.result?.length) {
          data.setState({ sections: a.result })
        }
      })

    const { accountNumber } = a
    let bundles: DocumentBundle[] = []

    clearDocuments.setState({ show: false })

    const r = await Promise.all(
      a.wsSummaries.map(({ worksheetId }) =>
        getDocumenBundelRequest({
          worksheetId,
          accountNumber,
        }),
      ),
    )

    if (r?.length) {
      bundles = r
        .filter((a) => a.result)
        .map((a) => a.result)
        .reverse()
    }

    const id = a.worksheetId ? a.worksheetId : a.wsSummaries[a.wsSummaries.length - 1].worksheetId

    let bundle = bundles[0]
    if (id) {
      bundle = bundles.filter((v) => v?.worksheetId === id)[0] || bundles[0]
      a.worksheetId = id
    }

    clearDocuments.setState({ show: true })

    updateBundleVersionForm(bundle)

    if (feedbackEnabled) {
      await addFeedback(data.getState().bundleKey)
    }

    const summary = a.wsSummaries.find((a) => a?.worksheetId === id)
    data.setState({ ...clearDocs, ...{ bundles, bundle, summary } } as DocumentData)
  }

  const feedbackMap: Record<string, DocumentFeedback[]> = {}

  async function addFeedback(bundleKey: string, force = false): Promise<DocumentFeedback[] | undefined> {
    if (!force && feedbackMap[bundleKey]) {
      return feedbackMap[bundleKey]
    }
    try {
      const r = await getSuperchartFeedbacksRequest({ bundleId: bundleKey })
      console.info('feedback list', bundleKey, r)

      if (r.result?.length) {
        feedbackMap[bundleKey] = r.result
        return feedbackMap[bundleKey]
      }
    } catch (e) {
      console.info('error getting feedback', e)
    }
    return Promise.resolve(undefined)
  }

  function getFeedback(bundleKey: string) {
    return feedbackMap[bundleKey] || []
  }

  const annotationApis: Record<string, AnnotationApi> = {}

  function getAnnotationApi(section: string, text: string) {
    const bundleId = data.getState()?.bundleKey
    const key = bundleId + section
    let api = annotationApis[key]
    if (api) {
      return api
    }
    const feedback = getFeedback(bundleId)
    annotationApis[key] = annotationApi({ text, section, bundleId, feedback })
    return annotationApis[key]
  }

  async function getBundleVersion({ bundleKey }: { bundleKey: string }) {
    data.setState({ ...clearDocs, bundleKey } as DocumentData)
    addFeedback(bundleKey)
    request(
      () => getDocumenBundelRequest({ bundleKey }),
      (bundle) => {
        if (bundle?.bundleVersions?.length) {
          updateBundleVersionForm(bundle)
        }

        data.setState({
          bundle,
        })
      },
      { bundleKey },
    )
  }
  const clearDocs: Partial<DocumentData> = {
    ref: null,
    content: null,
    pdf: null,
    superChart: null,
    demographics: null,
    autoCode: null,
    html: null
  }

  const bundleVersionForm = formApi({
    values: { bundle: null },
    fields: {
      bundle: {
        style: { width: 200 },
        list: [],
        listLabel: (b: BundleVersion) => `v${b.bundleVersion} - ${(b?.completeDate || '').substring(0, 10)}`,
        onChange: (v) => {
          if (v) {
            getBundleVersion(v)
          }
        },
      },
    },
  })

  function getBundleId() {
    return data.getState().bundle.bundleMasterId
  }

  function updateBundleVersionForm(bundle: DocumentBundle) {
    const versions = bundle?.bundleVersions || []
    bundleVersionForm.controls.bundle.list.setState({ value: versions })

    const version = versions[versions.length - 1]
    data.setState({ bundleKey: version?.bundleKey })

    postDocumentBundle2({ ...version, worksheetId: bundle?.worksheetId })
    bundleVersionForm.controls.bundle.value.setState({
      value: version,
    })
  }

  function getContentData(ref: DocumentRef, content: any): Partial<DocumentData> {
    clearDocs.ref = ref

    if (isDemographics(ref)) {
      if(ref.format === 'html') {
        return {
          ...clearDocs,
        ...{ html: content },
        }
      } else {
        return {
          ...clearDocs,
          ...{ demographics: content as DemographicsDocument },
        }
      }
    }

    if (isSuperChart(ref)) {
      return { ...clearDocs, ...{ superChart: content as any } }
    }

    if (isAutoCode(ref)) {
      return { ...clearDocs, ...{ autoCode: content as AutoCode } }
    }

    const format: KnownDocumentFormats = ref.format as KnownDocumentFormats

    switch (format) {
      case 'PDF':
      case 'pdf':
        return {
          ...clearDocs,
          ...{ pdf: content },
          //      ...{ pdf: `data:application/pdf;base64,${content}` },
        }
      case 'JSON':
      case 'json':
      case 'TXT':
      case 'txt':
        return { ...clearDocs, content }
      case 'HTML':
      case 'html':
        return { ...clearDocs, html: content }
      default:
        return { ...clearDocs, content }
    }
  }

  async function getContent(ref: DocumentRef) {
    const { key, bucket } = ref

    request(
      () =>
        getDocumentContentRequest({
          bundleMasterId: getBundleId(),
          markAsRead: true,
          filekey: key,
          bucket,
        }),
      (content) => data.setState(getContentData(ref, content) as DocumentData),
    )
  }

  function getCodingProfile() {
    const locationCode = data.getState().visit.locationCode
    request(
      () => Promise.resolve({ locationCode, result: {} }),
      (pdf) => data.setState({ pdf, content: null, superChart: null }),
    )
  }

  const rawDocumentWindows: Window[] = []
  function openInTab(ref: DocumentRef) {
    rawDocRequest(ref).then((w) => {
      if (w) {
        rawDocumentWindows.push(w)
      }
    })
  }

  function setSummary(summary: WorksheetSummary) {
    if (summary) {
      data.setState({ ...clearDocs, ...{ bundle: null, summary } } as DocumentData)

      const b = data.getState().bundles
      if (b?.length) {
        const bundle = b.find((v) => v?.worksheetId === summary.worksheetId)
        if (bundle) {
          updateBundleVersionForm(bundle)
          getBundleVersion({ bundleKey: bundle.bundleMasterId })
          data.setState({ bundle })
        }
      }
    }
  }

  function closeRawDocuments() {
    console.info('closing raw documents', rawDocumentWindows.length)
    rawDocumentWindows.forEach((w) => w && w.close())
    rawDocumentWindows.length = 0
  }

  return {
    data,
    openWindow,
    closeWindow,
    closeRawDocuments,
    getBundles,
    getBundleVersion,
    getContent,
    getCodingProfile,
    openInTab,
    setSummary,
    getFeedback,
    getAnnotationApi,
    setWorkflow,
    updateFeedback: (k) => addFeedback(k, true),
    validateAccount,
    bundleVersionForm,
  }
}

const closeChannel = new window.BroadcastChannel('documentCloseEvent')
const selectionChannel = new window.BroadcastChannel('documentSelectionEvent')

export const refreshSummary = new window.BroadcastChannel('refreshSummaryEvent')
refreshSummary.onmessage = (e) => {
  if (e.data?.worksheetId) {
    documentApi2.setSummary(e.data)
  }
}

export const accountValidation = new window.BroadcastChannel('accountValidationEvent')
export function onDocumentBundleSelected2(fn: (a: DocumentBundle) => void) {
  selectionChannel.onmessage = (a: MessageEvent) => {
    if (fn && a?.data) {
      fn(a.data)
    }
  }
}
export function postDocumentBundle2(a: { bundleVersion: string | number; completeDate: string; worksheetId?: string }) {
  if (a) {
    const { bundleVersion, completeDate, worksheetId } = a
    selectionChannel.postMessage({
      worksheetId,
      version: bundleVersion,
      createDate: completeDate,
    })
  }
}

export const documentApi2 = createDocumentApi()
const origin = window.location.origin

window.onmessage = (e: MessageEvent) => {
  if (e.origin !== origin) {
    return
  }

  const { data } = e
  if (!data) {
    return
  }

  if (data.event === 'documentBundleSelected') {
    return
  }

  if (data.event === 'validateAccount') {
    return
  }

  if (!data?.visit_id) {
    return
  }

  const session = getCurrentVisitSession(e.data.visit_id)
  const current = documentApi2.data.getState().visit
  if (session?.accountNumber !== current?.accountNumber) {
    documentApi2.data.setState({
      visit: session,
      bundle: null,
      error: null,
      bundles: [],
      treeData: null,
    })
  }
}
async function rawDocRequest(ref: DocumentRef): Promise<Window | undefined> {
  const isPdf = ref.key.indexOf('pdf') > -1
  const filekey = ref.key

  try {
    const r = await getDocumentUrlRequest({
      filekey,
      bucket: ref.bucket,
    })

    const url = r?.result?.url
    if (url) {
      if (isPdf) {
        const tab = window.open('')
        if (tab?.document) {
          tab.document.write(`
          <object data="${url}" type="application/pdf"> <embed src="${url}" type="application/pdf" /> </object>
          `)
          return tab
        }
      } else {
        return window.open(url, '_blank')
      }
    }
  } catch (_) {
    log.info('error getting document url', _)
  }
  return undefined
}
